import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { validateEmail } from '../../../utils/emailValidation';

const defaultForm = {
  name: '',
  email: '',
  phone: '',
  receives_email: true,
  receives_sms: false,
};

const RecipientFormModal = ({ recipient, onSave, onClose }) => {
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    if (recipient) {
      setForm({
        name: recipient?.name ?? '',
        email: recipient?.email ?? '',
        phone: recipient?.phone ?? '',
        receives_email: recipient?.receives_email ?? true,
        receives_sms: recipient?.receives_sms ?? false,
      });
    } else {
      setForm(defaultForm);
    }
    setEmailError(null);
  }, [recipient]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setSaveError(null);
    if (name === 'email') {
      setEmailError(validateEmail(value));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.name?.trim()) {
      setSaveError('Name is required.');
      return;
    }
    if (!form?.email?.trim() && !form?.phone?.trim()) {
      setSaveError('At least one of email or phone is required.');
      return;
    }
    const emailValidationError = validateEmail(form?.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ ...form, active: true }, recipient?.id ?? null);
    } catch (err) {
      setSaveError(err?.message ?? 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">
            {recipient ? 'Edit Person' : 'Add Person'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
          >
            <Icon name="X" size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
          {saveError && (
            <div className="flex items-start space-x-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
              <Icon name="AlertCircle" size={15} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
              <span>{saveError}</span>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={form?.name}
              onChange={handleChange}
              placeholder="e.g. Ruben"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Email</label>
            <input
              type="text"
              name="email"
              value={form?.email}
              onChange={handleChange}
              placeholder="e.g. admin@rpmdieselservices.com"
              className={`w-full px-3 py-2 text-sm bg-background border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors ${emailError ? 'border-destructive' : 'border-border'}`}
            />
            {emailError && (
              <p className="mt-1 text-xs text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} strokeWidth={2} />
                {emailError}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Phone</label>
            <input
              type="text"
              name="phone"
              value={form?.phone}
              onChange={handleChange}
              placeholder="e.g. 432-269-2905"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
          </div>

          {/* Alert Toggles */}
          <div className="space-y-3 pt-1">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Email Alerts</span>
                <p className="text-xs text-muted-foreground">Receive email notifications for new service requests</p>
              </div>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, receives_email: !prev?.receives_email }))}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${form?.receives_email ? 'bg-primary' : 'bg-muted'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form?.receives_email ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Text Alerts</span>
                <p className="text-xs text-muted-foreground">Receive SMS notifications for new service requests</p>
              </div>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, receives_sms: !prev?.receives_sms }))}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${form?.receives_sms ? 'bg-primary' : 'bg-muted'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form?.receives_sms ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {saving && <div className="w-3.5 h-3.5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />}
              <span>{saving ? 'Saving…' : recipient ? 'Save Changes' : 'Add Person'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipientFormModal;
